/* ============================================================
   Cleanly — gift lines in the cart.

   A bundle gift is added as a normal line item (its value is folded into
   the bundle's automatic discount), so the cart would otherwise show a
   price next to it. Two jobs here:

   1. While the bundle is intact — render the gift as a gift: the value
      struck through in red with a bold "חינם!" under it, and hide the
      "מתנה: ✓" line property (the price treatment already says it).
   2. If the shopper removes one of the bundle's main products — the gift
      is no longer earned. Its gift properties are stripped from the line,
      so it goes back to being an ordinary item at its normal price, in
      the cart AND at checkout.

   "Intact" is decided by the gift's own `_req` property (the variant ids
   and quantities the offer was built from), NOT by whether a discount
   happens to be applied — a discount code can legitimately replace the
   automatic bundle discount.
   ============================================================ */
(function () {
  var STYLE_ID = 'cl-gift-style';
  var GIFT_LABEL = 'מתנה';
  var cart = null;
  var busy = false;
  var lastRowCount = -1;

  function injectStyle() {
    if (document.getElementById(STYLE_ID)) return;
    var s = document.createElement('style');
    s.id = STYLE_ID;
    s.textContent =
      '.cl-gift-row .cart-items__unit-price-wrapper{display:none !important;}' +
      '.cl-gift-price{display:flex;flex-direction:column;align-items:flex-end;gap:1px;line-height:1.2;font-family:"Assistant",sans-serif;}' +
      '.cl-gift-price__was{color:#b71c1c;text-decoration:line-through;font-size:13px;font-weight:600;}' +
      '.cl-gift-price__free{font-size:15px;font-weight:800;color:#1d1d1f;}';
    (document.head || document.documentElement).appendChild(s);
  }

  function itemsByKey() {
    var m = {};
    if (cart && cart.items) {
      cart.items.forEach(function (i) { m[i.key] = i; });
    }
    return m;
  }

  /* Is the bundle this gift belongs to still fully in the cart? */
  function isEarned(gift) {
    var p = gift.properties || {};
    var bundleKey = p._bundle;
    var req = p._req;

    if (!req) {
      // Older line without a requirement list: fall back to "is the bundle
      // discount currently applied to any line of this bundle".
      if (!cart || !cart.items || !bundleKey) return true;
      return cart.items.some(function (i) {
        var q = i.properties || {};
        return q._bundle === bundleKey && i.line_level_total_discount > 0;
      });
    }

    return req.split(',').every(function (part) {
      var bits = part.split('x');
      var id = Number(bits[0]);
      var need = Number(bits[1] || 1);
      var have = 0;
      (cart.items || []).forEach(function (i) {
        var q = i.properties || {};
        if (i.variant_id === id && q._bundle === bundleKey && q._gift !== 'true') have += i.quantity;
      });
      return have >= need;
    });
  }

  function hideGiftLabel(row) {
    var props = row.querySelectorAll('.cart-items__property');
    for (var i = 0; i < props.length; i++) {
      var dt = props[i].querySelector('dt');
      if (dt && dt.textContent.replace(':', '').trim() === GIFT_LABEL) props[i].style.display = 'none';
    }
  }

  function paint() {
    injectStyle();
    var map = itemsByKey();
    var rows = document.querySelectorAll('.cart-items__table-row');

    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      var td = row.querySelector('.cart-items__price');
      if (!td) continue;

      var item = map[row.getAttribute('data-key')];
      var p = item ? (item.properties || {}) : {};
      var free = p._gift === 'true' && isEarned(item);

      if (p._gift === 'true') hideGiftLabel(row);

      if (free) {
        if (td.querySelector('.cl-gift-price')) { row.classList.add('cl-gift-row'); continue; }

        // Prefer the compare-at value (the gift's real worth, e.g. ₪79.00).
        var was = '';
        var unit = row.querySelector('.cart-items__unit-price-wrapper');
        if (unit) {
          var s = unit.querySelector('s');
          if (s && s.textContent.trim()) was = s.textContent.trim();
        }
        if (!was) was = (td.textContent || '').trim();
        if (!was) continue;

        if (!td.hasAttribute('data-cl-orig')) td.setAttribute('data-cl-orig', td.innerHTML);
        row.classList.add('cl-gift-row');
        td.innerHTML =
          '<span class="cl-gift-price"><s class="cl-gift-price__was">' + was +
          '</s><span class="cl-gift-price__free">חינם!</span></span>';
      } else {
        row.classList.remove('cl-gift-row');
        if (td.hasAttribute('data-cl-orig')) {
          td.innerHTML = td.getAttribute('data-cl-orig');
          td.removeAttribute('data-cl-orig');
        }
      }
    }
  }

  function fetchCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) { cart = c; return c; })
      .catch(function () { return null; });
  }

  /* Turn no-longer-earned gifts back into ordinary paid items. */
  function stripUnearned() {
    if (busy || !cart || !cart.items) return Promise.resolve(false);

    var stale = cart.items.filter(function (i) {
      var p = i.properties || {};
      return p._gift === 'true' && !isEarned(i);
    });
    if (!stale.length) return Promise.resolve(false);

    busy = true;
    var chain = Promise.resolve();
    stale.forEach(function (it) {
      chain = chain.then(function () {
        var keep = {};
        var src = it.properties || {};
        Object.keys(src).forEach(function (k) {
          if (k !== '_gift' && k !== '_req' && k !== '_bundle' && k !== '_ships_free' && k !== GIFT_LABEL) {
            keep[k] = src[k];
          }
        });
        if (!Object.keys(keep).length) keep = { '_cl': '' };
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: it.key, properties: keep })
        }).catch(function () {});
      });
    });

    return chain
      .then(fetchCart)
      .then(function (c) {
        busy = false;
        paint();
        if (c) {
          document.dispatchEvent(new CustomEvent('cart:update', {
            bubbles: true,
            detail: { resource: c, sourceId: 'cl-cart-gifts', data: { source: 'cl-cart-gifts', itemCount: c.item_count } }
          }));
        }
        return true;
      })
      .catch(function () { busy = false; return false; });
  }

  function sync() {
    return fetchCart().then(function () {
      paint();
      lastRowCount = document.querySelectorAll('.cart-items__table-row').length;
      return stripUnearned();
    });
  }

  var syncTimer;
  function scheduleSync() {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(sync, 200);
  }

  var paintTimer;
  function schedulePaint() {
    clearTimeout(paintTimer);
    paintTimer = setTimeout(function () {
      paint();
      // Safety net: the theme can re-render the cart without us seeing a
      // cart:update event. If the number of lines changed, re-check the
      // server so a removed bundle product is always noticed.
      var n = document.querySelectorAll('.cart-items__table-row').length;
      if (n !== lastRowCount) { lastRowCount = n; scheduleSync(); }
    }, 30);
  }

  if (document.readyState !== 'loading') sync();
  else document.addEventListener('DOMContentLoaded', sync);

  document.addEventListener('cart:update', function (e) {
    if (e && e.detail && e.detail.sourceId === 'cl-cart-gifts') { schedulePaint(); return; }
    scheduleSync();
  });
  document.addEventListener('shopify:section:load', schedulePaint);

  // The cart drawer re-renders by morphing nodes in place, so watch the DOM.
  try {
    new MutationObserver(schedulePaint).observe(document.documentElement, { childList: true, subtree: true });
  } catch (e) {}

  [300, 900, 1800].forEach(function (d) { setTimeout(sync, d); });
})();
